import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Popup, MapContext } from '@geo/react-grabmaps-gl';
import styles from './floating-box.less';
import {
  POPUP_REQUIRED_X_OFFSET,
  MARKER_SIZE,
  OFFSET_PADDING,
} from '../../constants/marker';
import { useViewPort } from '../../context/view-port';

export const useMapContext = () => {
  const mapContext = useContext(MapContext);
  if (!mapContext) throw new Error('This hooks must use inside GrabMap');
  return mapContext;
};

// This function is not reusable, it made for this project specifically
/**
 * @param {{x: number, y: number}} centerPoint
 * @param {{width: number, height: number}} contentDimension
 * @param {{width: number, height: number}} boundingBox
 */
export const calculateOffset = (centerPoint, contentDimension, boundingBox) => {
  const { x: targetX, y: targetY } = centerPoint;
  const { width: contentWidth, height: contentHeight } = contentDimension;
  const { width: boundingWidth, height: boundingHeight } = boundingBox;

  const spaceX = (contentWidth + MARKER_SIZE.width) / 2 + OFFSET_PADDING.x;
  const spaceY = contentHeight / 2 + MARKER_SIZE.height + OFFSET_PADDING.y;

  const canLeft = targetX - spaceX > 0;
  const canRight = targetX + spaceX < boundingWidth;

  const canTop = targetY - spaceY > 0;
  const canBottom = targetY + spaceY < boundingHeight;

  const offsetLeft = POPUP_REQUIRED_X_OFFSET - spaceX;
  const offsetRight = POPUP_REQUIRED_X_OFFSET + spaceX;
  const offsetTop = -(
    contentHeight / 2 +
    MARKER_SIZE.height +
    OFFSET_PADDING.y
  );
  const offsetBottom = contentHeight / 2 + OFFSET_PADDING.y;

  if (canBottom) {
    if (canLeft && canRight) return [POPUP_REQUIRED_X_OFFSET, offsetBottom];
    if (canRight) return [offsetRight, offsetBottom];
    if (canLeft) return [offsetLeft, offsetBottom];
  }

  if (canTop) {
    if (canLeft && canRight) return [POPUP_REQUIRED_X_OFFSET, offsetTop];
    if (canRight) return [offsetRight, offsetTop];
    if (canLeft) return [offsetLeft, offsetTop];
  }

  if (canRight) return [offsetRight, 0];
  if (canLeft) return [offsetLeft, 0];

  return [0, 0];
};

const FloatingBox = ({ coordinates, children }) => {
  const { dimension } = useViewPort();
  const { map } = useMapContext();
  const contentRef = useRef();

  const [offset, setOffset] = useState([0, 0]);

  useEffect(() => {
    const element = contentRef.current;
    const height = element.clientHeight;
    setOffset([POPUP_REQUIRED_X_OFFSET, height / 2 + OFFSET_PADDING.y]);
  }, []);

  const findOffset = useCallback(
    () => {
      const element = contentRef.current;

      const { x, y } = map.project(coordinates);

      const contentWidth = element.clientWidth;
      const contentHeight = element.clientHeight;

      const mapWidth = map.transform.width;
      const mapHeight = map.transform.height;

      const newOffset = calculateOffset(
        { x: x - dimension.blockerWidth, y },
        { width: contentWidth, height: contentHeight },
        {
          width: mapWidth - dimension.blockerWidth,
          height: mapHeight - dimension.blockerHeight - 48,
        },
      );

      return newOffset;
    },
    [map, coordinates, dimension],
  );

  useEffect(
    () => {
      const moveHandler = () => {
        const newOffset = findOffset();
        setOffset(newOffset);
      };
      map.on('move', moveHandler);

      return () => {
        map.off('move', moveHandler);
      };
    },
    [findOffset],
  );

  return (
    <Popup
      className={styles.popup}
      coordinates={coordinates}
      anchor="center"
      offset={offset}
    >
      <div ref={contentRef}>{children}</div>
    </Popup>
  );
};

FloatingBox.propTypes = {
  coordinates: PropTypes.arrayOf(PropTypes.number).isRequired,
  children: PropTypes.node,
};

FloatingBox.defaultProps = {
  children: null,
};

export default FloatingBox;
